/**
 * The practice's rooms: the consult rooms, imaging rooms and so on that appointments can be booked into, so the
 * calendar can warn before two appointments are put in the same room at the same time.
 *
 * Rooms are archived rather than deleted, so an appointment that used one still shows its name. Only Practice
 * Admins reach this screen; the settings rail hides it from everyone else, and the API refuses their changes.
 */
app.component("practiceRooms", {
	templateUrl: "app/src/js/components/practiceRooms/practiceRooms.html",
	controller: "practiceRoomsController",
	bindings: { practiceId: "<" },
});

app.controller("practiceRoomsController", [
	"$scope",
	"PracticeService",
	"ExceptionAPI",
	"Session",
	"$modal",
	"$q",
	function ($scope, PracticeService, ExceptionAPI, Session, $modal, $q) {
		$scope.API = PracticeService;
		$scope.rooms = [];
		$scope.isLoading = false;
		$scope.loadError = null;
		$scope.saveStatus = "";
		// An object, so the filter box inside the ng-if blocks writes to this scope rather than its own.
		$scope.search = { text: "" };
		$scope.newRoom = blankRoom();

		// The same colour picker the appointment types use.
		$scope.colourOptions = {
			hue: true,
			saturation: false,
			lightness: false,
			alpha: false,
			format: "hex",
			round: false,
			inline: false,
			inputClass: "form-control",
			dynamicHue: false,
			dynamicLightness: false,
			dynamicSaturation: false,
			dynamicAlpha: false,
		};

		function blankRoom() {
			return { name: "", colour: "00BCD4", description: "" };
		}

		this.$onInit = function () {
			// The settings dialog can be pointed at any practice the user belongs to, so every call names the
			// practice rather than relying on the session.
			$scope.practiceId = this.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find((option) => option.id === $scope.practiceId)
				|| Session.practice || {};
			$scope.practiceName = practice.name || "";

			loadRooms();
		};

		function loadRooms() {
			$scope.isLoading = true;
			$scope.loadError = null;
			$scope.API.getRooms($scope.practiceId)
				.then(function (response) {
					showRooms(response.data);
				})
				.catch(function (error) {
					$scope.loadError = ExceptionAPI.serverMessage(error)
						|| "The rooms could not be loaded. Close settings and try again.";
				})
				.finally(function () {
					$scope.isLoading = false;
				});
		}

		// Adding or updating a room answers with the whole list, so every change ends here.
		function showRooms(rooms) {
			$scope.rooms = (rooms || []).map(function (room) {
				room.is_active = room.is_active !== false;
				return room;
			});
			rememberSaved();
		}

		// --- What is on the list ---

		$scope.visibleRooms = function () {
			const term = ($scope.search.text || "").trim().toLowerCase();
			if (!term) {
				return $scope.rooms;
			}
			return $scope.rooms.filter((room) => (room.name || "").toLowerCase().indexOf(term) !== -1);
		};

		$scope.activeCount = function () {
			return $scope.rooms.filter((room) => room.is_active).length;
		};

		$scope.archivedCount = function () {
			return $scope.rooms.length - $scope.activeCount();
		};

		// Two active rooms can't share a name, or nobody could tell them apart in the booking screen. An archived
		// room's name is free to reuse.
		$scope.isDuplicateName = function (name, roomId, isActive) {
			const trimmed = (name || "").trim().toLowerCase();
			if (!trimmed || isActive === false) {
				return false;
			}
			return $scope.rooms.some((room) => room.room_id !== roomId
				&& room.is_active
				&& (room.name || "").trim().toLowerCase() === trimmed);
		};

		// --- Editing what is already there ---

		// Each room as last saved, so Save sends only what changed.
		let saved = {};

		function rememberSaved() {
			saved = {};
			$scope.rooms.forEach(function (room) {
				saved[room.room_id] = snapshot(room);
			});
		}

		function snapshot(room) {
			return { name: room.name, colour: room.colour, description: room.description || "", is_active: room.is_active };
		}

		function hasChanged(room) {
			return !angular.equals(saved[room.room_id], snapshot(room));
		}

		$scope.hasUnsavedChanges = function () {
			return $scope.rooms.some(hasChanged);
		};

		// Drops a stale "saved" tick the moment something else is edited.
		$scope.$watch($scope.hasUnsavedChanges, function (dirty) {
			if (dirty) {
				$scope.saveStatus = "";
			}
		});

		function isRoomValid(room) {
			return !!(room.name || "").trim() && !$scope.isDuplicateName(room.name, room.room_id, room.is_active);
		}

		$scope.canSave = function () {
			return $scope.hasUnsavedChanges() && $scope.rooms.every(isRoomValid);
		};

		$scope.save = function () {
			if (!$scope.hasUnsavedChanges()) {
				$scope.saveStatus = "No changes to save";
				return;
			}
			if (!$scope.canSave()) {
				$scope.saveStatus = "Error: fix the highlighted names first";
				return;
			}

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			const changed = $scope.rooms.filter(hasChanged);
			// One at a time, so the list from the last answer is the one shown.
			let chain = $q.resolve();
			changed.forEach(function (room) {
				chain = chain.then(() => $scope.API.updateRoom(roomRequest(room), $scope.practiceId));
			});
			chain
				.then(function (response) {
					showRooms(response.data);
					$scope.saveStatus = "Rooms saved";
				})
				.catch(function (error) {
					$scope.saveStatus = "Error saving rooms";
					showError(error, "Your changes could not be saved.");
				})
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		};

		function roomRequest(room) {
			return {
				room_id: room.room_id,
				name: (room.name || "").trim(),
				colour: room.colour,
				description: room.description,
				sequence_number: room.sequence_number,
				is_active: room.is_active,
			};
		}

		// --- Adding one ---

		$scope.canAddRoom = function () {
			return !!($scope.newRoom.name || "").trim() && !$scope.isDuplicateName($scope.newRoom.name, null, true);
		};

		$scope.addRoom = function () {
			// A second click while the first add is in flight would add the room twice.
			if (!$scope.canAddRoom() || $scope.API.isProcessing) {
				return;
			}
			// Adding answers with the saved list, which would throw away edits not yet saved.
			if ($scope.hasUnsavedChanges()) {
				$scope.saveStatus = "Error: save your changes before adding a room";
				return;
			}

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";
			$scope.API.addRoom({
				name: $scope.newRoom.name.trim(),
				colour: $scope.newRoom.colour,
				description: $scope.newRoom.description,
			}, $scope.practiceId)
				.then(function (response) {
					showRooms(response.data);
					$scope.newRoom = blankRoom();
					$scope.saveStatus = "Room added";
				})
				.catch(function (error) {
					$scope.saveStatus = "Error adding room";
					showError(error, "The room could not be added.");
				})
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		};

		function showError(error, lead) {
			if (error) {
				console.error("Room request failed:", error.status, ExceptionAPI.serverMessage(error));
			}
			$scope.alertMessage = lead;
			$scope.alertDetail = ExceptionAPI.serverMessage(error);
			$scope.serverErrorModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
				show: true,
				title: "CatTrax has encountered an error",
			});
		}
	},
]);
